import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import * as net from 'util/Net';

import {SCIENTIFIC_MODEL} from '../ScientificModelConstants';

export interface BulkPredictionItem {
  id: string;
  entityType: string;
}

const INPUT_TYPE_TO_FOLDER_ITEM_TYPE: Record<string, OligoAwareFolderItemType> = {
  amino_acid_sequence: ModelTypeConstants.PROTEIN,
  molecule: ModelTypeConstants.MOLECULE,
};

export function getAvailableTypesForModel(inputTypes: Array<string>): Array<OligoAwareFolderItemType> {
  return inputTypes
    .map((t) => INPUT_TYPE_TO_FOLDER_ITEM_TYPE[t])
    .filter((t): t is OligoAwareFolderItemType => t != null);
}

export function isEntityTypeCompatible(entityType: string, inputTypes: Array<string>): boolean {
  const compatibleFolderItemTypes = getAvailableTypesForModel(inputTypes);
  return compatibleFolderItemTypes.includes(entityType as OligoAwareFolderItemType);
}

type PredictionConfigBuilder = (item: BulkPredictionItem) => Record<string, unknown> | Array<string>;

const aaSequenceConfigBuilder: PredictionConfigBuilder = (item) => ({aaSequenceId: item.id});
const multimerListConfigBuilder: PredictionConfigBuilder = (item) => [item.id];

const PREDICTION_CONFIG_BUILDERS: Record<string, PredictionConfigBuilder> = {
  [SCIENTIFIC_MODEL.ALPHAFOLD]: aaSequenceConfigBuilder,
  [SCIENTIFIC_MODEL.OPENFOLD2]: aaSequenceConfigBuilder,
  [SCIENTIFIC_MODEL.OPENFOLD3]: aaSequenceConfigBuilder,
  [SCIENTIFIC_MODEL.CHAI1]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.BOLTZ2]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.PROTENIX]: multimerListConfigBuilder,
};

export function buildPredictionConfigs(
  model: string,
  items: Array<BulkPredictionItem>
): Array<Record<string, unknown> | Array<string>> {
  const builder = PREDICTION_CONFIG_BUILDERS[model] ?? aaSequenceConfigBuilder;
  return items.map((item) => builder(item));
}

export interface BulkPredictionSubmitParams {
  folderId: string;
  model: string;
  items: Array<BulkPredictionItem>;
  modelParams?: Record<string, unknown>;
  source?: PredictionBatchSource;
}

export async function submitBulkPrediction(
  params: BulkPredictionSubmitParams
): Promise<{predictionBatchId: string}> {
  const {folderId, model, items, modelParams, source} = params;
  const predictionConfigs = buildPredictionConfigs(model, items);

  const inputConfig: Record<string, unknown> = {
    predictionConfigs,
    ...modelParams,
  };

  return await net.postJSONPromise({
    url: '/1/api/model-hub/prediction-batch',
    data: {
      folderId,
      model,
      inputConfig,
      source,
    },
  });
}
